import React, { useState, useEffect } from 'react'
import { Label, Button } from 'reactstrap'
import { useNavigate } from 'react-router-dom'
import { getIdData } from '../../lib/studentinfo'
import { getFile } from '../../lib/file'
import { useLayout } from '../../Hooks/useLayout'
import { useRoleBasedUrl } from '../../Hooks/useRoleBasedUrl'

// Default avatar image
const emptyAvatar = require('../../assets/images/user/user-dp.png')

const StudentInfoCard = React.forwardRef(({ studentId, isProfile = false }, ref) => {
	const [studentInfo, setStudentInfo] = useState(null)
	const [avatar, setAvatar] = useState(null)
	const [loading, setLoading] = useState(false)
	const [isDarkMode, setIsDarkMode] = useState(false)
	const navigate = useNavigate()
	const layout = useLayout()
	const role = useRoleBasedUrl()

	const fetchStudentData = async () => {
		if (!studentId) return

		try {
			setLoading(true)
			const std = await getIdData(studentId)
			if (std && std.length > 0) {
				const student = std[0]
				setStudentInfo(student)

				// Fetch avatar if available
				if (student.avatar_url) {
					try {
						const avatarData = await getFile(student.avatar_url)
						setAvatar(avatarData.publicUrl)
					} catch (error) {
						console.error('Error fetching avatar:', error)
						setAvatar(emptyAvatar)
					}
				} else {
					setAvatar(emptyAvatar)
				}
			}
		} catch (error) {
			console.error('Error fetching student data:', error)
		} finally {
			setLoading(false)
		}
	}

	const handleUpdate = () => {
		navigate(`${process.env.PUBLIC_URL}/${role}/class-management/student-update/${studentId}/${layout}`)
	}

	useEffect(() => {
		if (studentId) {
			fetchStudentData()
		}
	}, [studentId])

	useEffect(() => {
		const checkDarkMode = () => {
			setIsDarkMode(document.body.classList.contains('dark-only'))
		}
		checkDarkMode()
		const observer = new MutationObserver(checkDarkMode)
		observer.observe(document.body, {
			attributes: true,
			attributeFilter: ['class'],
		})
		return () => observer.disconnect()
	}, [])

	if (loading) {
		return (
			<div
				className="card mb-4 shadow-sm"
				style={{
					borderRadius: '8px',
					backgroundColor: isDarkMode ? '#262932' : '#fff',
				}}
			>
				<div
					className="card-header border-bottom d-flex justify-content-between align-items-center"
					style={{ backgroundColor: isDarkMode ? '#262932' : '#fff' }}
				>
					<div className="d-flex align-items-center">
						{!isProfile && (
							<button
								type="button"
								className="btn btn-link p-0 me-2"
								data-bs-toggle="collapse"
								data-bs-target="#collapseStudentInfo"
								aria-expanded="false"
								aria-controls="collapseStudentInfo"
							>
								<i className="fa fa-chevron-down"></i>
							</button>
						)}
						<h5
							className="mb-0 d-inline-block"
							style={{
								color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
							}}
						>
							Student Information
						</h5>
					</div>
				</div>
				<div className={isProfile ? '' : 'collapse'} id="collapseStudentInfo">
					<div
						className="card-body"
						style={{ backgroundColor: isDarkMode ? '#262932' : '#fff' }}
					>
						<div className="text-center py-4">
							<div className="spinner-border text-primary" role="status">
								<span className="visually-hidden">Loading...</span>
							</div>
							<p
								className="mt-2"
								style={{
									color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
								}}
							>
								Loading student data...
							</p>
						</div>
					</div>
				</div>
			</div>
		)
	}

	if (!studentInfo) {
		return (
			<div
				className="card mb-4 shadow-sm"
				style={{
					borderRadius: '8px',
					backgroundColor: isDarkMode ? '#262932' : '#fff',
				}}
			>
				<div
					className="card-header border-bottom d-flex justify-content-between align-items-center"
					style={{ backgroundColor: isDarkMode ? '#262932' : '#fff' }}
				>
					<div className="d-flex align-items-center">
						{!isProfile && (
							<button
								type="button"
								className="btn btn-link p-0 me-2"
								data-bs-toggle="collapse"
								data-bs-target="#collapseStudentInfo"
								aria-expanded="false"
								aria-controls="collapseStudentInfo"
							>
								<i className="fa fa-chevron-down"></i>
							</button>
						)}
						<h5
							className="mb-0 d-inline-block"
							style={{
								color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
							}}
						>
							Student Information
						</h5>
					</div>
				</div>
				<div className={isProfile ? '' : 'collapse'} id="collapseStudentInfo">
					<div
						className="card-body"
						style={{ backgroundColor: isDarkMode ? '#262932' : '#fff' }}
					>
						<div className="text-center py-4">
							<h5
								style={{
									color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
								}}
							>
								Student not found
							</h5>
						</div>
					</div>
				</div>
			</div>
		)
	}

	return (
		<div
			ref={ref}
			className="card mb-4 shadow-sm"
			style={{
				borderRadius: '8px',
				backgroundColor: isDarkMode ? '#262932' : '#fff',
			}}
		>
			<div
				className="card-header border-bottom d-flex justify-content-between align-items-center"
				style={{ backgroundColor: isDarkMode ? '#262932' : '#fff' }}
			>
				<div className="d-flex align-items-center">
					{!isProfile && (
						<button
							type="button"
							className="btn btn-link p-0 me-2"
							data-bs-toggle="collapse"
							data-bs-target="#collapseStudentInfo"
							aria-expanded="false"
							aria-controls="collapseStudentInfo"
						>
							<i className="fa fa-chevron-down"></i>
						</button>
					)}
					<h5
						className="mb-0 d-inline-block"
						style={{
							color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
						}}
					>
						Student Information
					</h5>
				</div>
			</div>
			<div className={isProfile ? '' : 'collapse'} id="collapseStudentInfo">
				<div
					className="card-body"
					style={{ backgroundColor: isDarkMode ? '#262932' : '#fff' }}
				>
					{/* Student Header Info */}
					<div className="row mb-4">
						<div className="col-3">
							<div className="student-img text-center">
								<img
									className="rounded-circle"
									style={{ height: '100px', width: '100px', objectFit: 'cover' }}
									alt="Student Avatar"
									src={avatar || emptyAvatar}
								/>
							</div>
						</div>
						<div className="col-9">
							<h3
								className="mb-2"
								style={{
									color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'inherit',
								}}
							>
								{studentInfo.name ? `${studentInfo.name} ${studentInfo.surname || ''}`.trim() : 'Student Name Not Found'}
							</h3>
							<p
								className="mb-1"
								style={{
									color: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : '#6c757d',
								}}
							>
								{studentInfo.schoolname ? `${studentInfo.schoolname} - ${studentInfo.class || ''}` : 'School Information Not Found'}
							</p>
							{studentInfo.email && (
								<p
									className="mb-0"
									style={{
										color: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : '#6c757d',
									}}
								>
									<i className="fa fa-envelope me-2"></i>
									{studentInfo.email}
								</p>
							)}
						</div>
					</div>

					{/* Student Details */}
					<div className="row">
						<div className="col-lg-4 col-md-6 mb-3">
							<div className="form-group">
								<Label
									className="form-control-label fw-bold"
									style={{
										color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'inherit',
									}}
								>
									Graduation Year
								</Label>
								<p
									className="break-line"
									style={{
										color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
									}}
								>
									{studentInfo.graduateyear || 'N/A'}
								</p>
							</div>
						</div>
						<div className="col-lg-4 col-md-6 mb-3">
							<div className="form-group">
								<Label
									className="form-control-label fw-bold"
									style={{
										color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'inherit',
									}}
								>
									GPA
								</Label>
								<p
									className="break-line"
									style={{
										color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
									}}
								>
									{studentInfo.gpa || 'N/A'}
								</p>
							</div>
						</div>
						<div className="col-lg-4 col-md-6 mb-3">
							<div className="form-group">
								<Label
									className="form-control-label fw-bold"
									style={{
										color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'inherit',
									}}
								>
									SAT / PSAT
								</Label>
								<p
									className="break-line"
									style={{
										color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
									}}
								>
									{studentInfo.satpsat || 'N/A'}
								</p>
							</div>
						</div>
						<div className="col-lg-4 col-md-6 mb-3">
							<div className="form-group">
								<Label
									className="form-control-label fw-bold"
									style={{
										color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'inherit',
									}}
								>
									Practice SAT
								</Label>
								<p
									className="break-line"
									style={{
										color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
									}}
								>
									{studentInfo.statetest || 'N/A'}
								</p>
							</div>
						</div>
						<div className="col-lg-4 col-md-6 mb-3">
							<div className="form-group">
								<Label
									className="form-control-label fw-bold"
									style={{
										color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'inherit',
									}}
								>
									Major
								</Label>
								<p
									className="break-line"
									style={{
										color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
									}}
								>
									{studentInfo.major || 'N/A'}
								</p>
							</div>
						</div>
						<div className="col-lg-4 col-md-6 mb-3">
							<div className="form-group">
								<Label
									className="form-label fw-bold"
									style={{
										color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'inherit',
									}}
								>
									Career
								</Label>
								<p
									className="break-line"
									style={{
										color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
									}}
								>
									{studentInfo.career || 'N/A'}
								</p>
							</div>
						</div>
						<div className="col-lg-4 col-md-6 mb-3">
							<div className="form-group">
								<Label
									className="form-label fw-bold"
									style={{
										color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'inherit',
									}}
								>
									Five Adjectives
								</Label>
								<p
									className="break-line"
									style={{
										color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
									}}
								>
									{studentInfo.fiveAdjectives || 'N/A'}
								</p>
							</div>
						</div>
						<div className="col-lg-4 col-md-6 mb-3">
							<div className="form-group">
								<Label
									className="form-label fw-bold"
									style={{
										color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'inherit',
									}}
								>
									About my High School
								</Label>
								<p
									className="break-line"
									style={{
										color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
									}}
								>
									{studentInfo.hsSchoolStatistics || 'N/A'}
								</p>
							</div>
						</div>
						<div className="col-lg-4 col-md-6 mb-3">
							<div className="form-group">
								<Label
									className="form-label fw-bold"
									style={{
										color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'inherit',
									}}
								>
									Top Three Colleges Choices
								</Label>
								<p
									className="break-line"
									style={{
										color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
									}}
								>
									{studentInfo.topThreeColleges || 'N/A'}
								</p>
							</div>
						</div>
						<div className="col-lg-4 col-md-6 mb-3">
							<div className="form-group">
								<Label
									className="form-label fw-bold"
									style={{
										color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'inherit',
									}}
								>
									Letter of Recommendation
								</Label>
								<p
									className="break-line"
									style={{
										color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
									}}
								>
									{studentInfo.lor || 'N/A'}
								</p>
							</div>
						</div>
						<div className="col-lg-4 col-md-6 mb-3">
							<div className="form-group">
								<Label
									className="form-label fw-bold"
									style={{
										color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'inherit',
									}}
								>
									Activities
								</Label>
								<p
									className="break-line"
									style={{
										color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
									}}
								>
									{studentInfo.activities || 'N/A'}
								</p>
							</div>
						</div>
						<div className="col-lg-4 col-md-6 mb-3">
							<div className="form-group">
								<Label
									className="form-label fw-bold"
									style={{
										color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'inherit',
									}}
								>
									College Essay
								</Label>
								<p
									className="break-line"
									style={{
										color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
									}}
								>
									{studentInfo.collegeEssay || 'N/A'}
								</p>
							</div>
						</div>
						<div className="col-lg-4 col-md-6 mb-3">
							<div className="form-group">
								<Label
									className="form-label fw-bold"
									style={{
										color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'inherit',
									}}
								>
									Counselor's Comment
								</Label>
								<p
									className="break-line"
									style={{
										color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
									}}
								>
									{studentInfo.instructorNote || 'N/A'}
								</p>
							</div>
						</div>
						<div className="col-lg-4 col-md-6 mb-3">
							<div className="form-group">
								<Label
									className="form-label fw-bold"
									style={{
										color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'inherit',
									}}
								>
									Student's Comment
								</Label>
								<p
									className="break-line"
									style={{
										color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
									}}
								>
									{studentInfo.studentNote || 'N/A'}
								</p>
							</div>
						</div>
					</div>

					{!isProfile && (
						<Button color="primary" size="sm" onClick={handleUpdate}>
							<i className="fa fa-edit me-2"></i>
							Update
						</Button>
					)}
				</div>
			</div>
		</div>
	)
})

export { StudentInfoCard }
